import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Users, TrendingUp, Clock, BarChart3, Sparkles, Download, Loader2, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, BarChart, Bar, AreaChart, Area,
} from 'recharts';

import {
  formatScore,
  SegmentBreakdownChart,
  DriverScoreGauge,
  KeyMetricsTile,
  AIInsightsTile,
  QuestionSummaryCard,
} from '@/components/dashboard/DashboardWidgets';

/* ─── Constants ──────────────────────────────────────────── */
const A4_RATIO = 297 / 210;
const COLORS = ['#5AA2AE', '#8b5cf6', '#f59e0b', '#ef4444', '#22c55e', '#3b82f6', '#ec4899', '#06b6d4', '#14b8a6', '#a855f7'];

/* Extend trend chart data to include the current month */
function extendTrendToNow(chartData) {
  if (!chartData || chartData.length === 0) return chartData;
  const lastMonth = chartData[chartData.length - 1]?.month;
  if (!lastMonth) return chartData;
  const now = new Date();
  const currentLabel = now.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  if (lastMonth === currentLabel) return chartData;
  const lastDate = new Date(lastMonth + ' 1');
  if (isNaN(lastDate.getTime())) return chartData;
  const extended = [...chartData];
  const lastPt = chartData[chartData.length - 1];
  const cursor = new Date(lastDate);
  cursor.setMonth(cursor.getMonth() + 1);
  while (cursor <= now) {
    const label = cursor.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    // Carry forward last known values so the line extends to the current month
    const pt = { ...lastPt, month: label };
    extended.push(pt);
    cursor.setMonth(cursor.getMonth() + 1);
    if (extended.length > 60) break;
  }
  return extended;
}

/* Extend growth data (daily date-keyed) to include today */
function extendGrowthToNow(growthData) {
  if (!growthData || growthData.length === 0) return growthData;
  const lastEntry = growthData[growthData.length - 1];
  const lastDate = lastEntry?.date;
  if (!lastDate) return growthData;
  const today = new Date().toISOString().slice(0, 10);
  if (lastDate >= today) return growthData;
  // Add a point for today carrying forward the last cumulative values
  return [...growthData, {
    date: today,
    admin: lastEntry.admin,
    user: lastEntry.user,
    self: lastEntry.self,
    anonymous: lastEntry.anonymous,
    total: lastEntry.total,
  }];
}

/* ─── Page Wrappers ──────────────────────────────────────── */
const SidebarBar = ({ showLogo }) => (
  <div className="h-full w-full relative overflow-hidden flex flex-col" style={{ background: 'linear-gradient(180deg, #5AA2AE 0%, #3D7A84 40%, #1a3f47 100%)' }}>
    <div className="flex-1" />
    {showLogo && (
      <div className="px-3 pb-4 flex-shrink-0">
        <img src="/projectai-logo-white.png" alt="ProjectAI" className="h-5 opacity-80" />
      </div>
    )}
  </div>
);

const HeaderBar = ({ title, date }) => (
  <div className="flex items-center justify-between px-5 border-b border-slate-200 bg-white flex-shrink-0" style={{ height: 38 }}>
    <span className="font-bold text-slate-800 tracking-wide" style={{ fontSize: 15 }}>
      {(() => {
        const parts = title.split(' | ');
        if (parts.length > 1) {
          return <>{<span style={{ color: '#3D7A84' }}>{parts[0]}</span>} | {parts.slice(1).join(' | ')}</>;
        }
        return <span style={{ color: '#3D7A84' }}>{title}</span>;
      })()}
    </span>
    <span className="text-[10px] text-slate-400">{date}</span>
  </div>
);

const FooterBar = ({ pageNum, totalPages }) => (
  <div className="flex items-center justify-between px-5 flex-shrink-0" style={{ height: 32, background: 'linear-gradient(90deg, #3D7A84 0%, #2D5A63 100%)' }}>
    <div className="flex items-center gap-2">
      <img src="/pulse-square-logo.png" alt="Pulse" className="h-[18px] w-[18px] object-contain" />
      <span className="text-[10px] text-white/90 tracking-wide" style={{ fontWeight: 700 }}>Pulse by ProjectAI</span>
      <span className="text-[10px] text-white/50">&nbsp;|&nbsp; Stakeholder Intelligence</span>
    </div>
    <span className="text-[10px] text-white/70 font-medium">Page {pageNum} of {totalPages}</span>
  </div>
);

const SidebarPage = ({ children, showLogo = false }) => (
  <div className="h-full w-full flex bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
    <div style={{ width: '22%' }} className="flex-shrink-0 h-full"><SidebarBar showLogo={showLogo} /></div>
    <div style={{ width: '78%' }} className="h-full overflow-hidden">{children}</div>
  </div>
);

const ContentPage = ({ title, date, children, pageNum, totalPages }) => (
  <div className="h-full w-full flex flex-col bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
    <HeaderBar title={title} date={date} />
    <div className="flex-1 overflow-hidden px-5 py-3">{children}</div>
    <FooterBar pageNum={pageNum} totalPages={totalPages} />
  </div>
);

/* ════════════════════════════════════════════════════════════
   SLIDE 1: Cover
   ════════════════════════════════════════════════════════════ */
const CoverSlide = ({ data }) => (
  <SidebarPage showLogo>
    <div className="h-full flex flex-col justify-between p-8">
      <div className="flex-1 flex flex-col justify-center">
        <img src="/pulse-logo-with-name.png" alt="Pulse" className="h-8 object-contain object-left mb-6" />
        <h1 className="text-4xl font-bold text-slate-900 leading-tight mb-2">{data.project_name}</h1>
        <div className="w-20 h-0.5 bg-[#5AA2AE] mb-3" />
        <p className="text-lg text-[#5AA2AE] mb-1">Stakeholder Intelligence Report</p>
        <p className="text-xs text-slate-400">{data._dateFormatted}</p>
      </div>
      <div>
        <div className="w-full h-px bg-slate-200 mb-2" />
        <span className="text-[10px] text-slate-400 font-medium">Pulse by ProjectAI</span>
      </div>
    </div>
  </SidebarPage>
);

/* ════════════════════════════════════════════════════════════
   SLIDE 2: Disclaimer
   ════════════════════════════════════════════════════════════ */
const DISCLAIMER = [
  'This report was compiled using data provided by project administrators and respondents using <b>Pulse</b> by ProjectAI. Artificial intelligence has been used to aggregate and anonymise insights presented herein.',
  'The analysis contained in this report should be viewed as broadly indicative of the feedback received and is intended to support - not replace - professional judgement. It does not constitute advice of any kind.',
  'Dashboard content and this report may be filtered based on your specific permissions. As a result, the metrics and insights reported may differ between users.',
  'ProjectAI Asia-Pacific Pty Ltd and its associated companies accept no liability whatsoever for any decisions, actions, or omissions taken by any person or organisation as a result of, or in reliance upon, the information contained in this report or the use of the Pulse software platform.',
  'By using Pulse, all customers and users agree to indemnify and hold harmless ProjectAI Asia-Pacific Pty Ltd, its associated companies, directors, officers, employees, and agents from and against any and all claims, losses, damages, liabilities, costs, and expenses (including legal fees) arising out of or in connection with the use of the Pulse software or any reports generated by it.',
];

const DisclaimerSlide = () => (
  <SidebarPage showLogo>
    <div className="h-full flex flex-col justify-between p-8 pt-10">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-6">Disclaimer</h1>
        <div className="space-y-6 max-w-xl">{DISCLAIMER.map((p, i) => <p key={i} className="text-xs text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: p }} />)}</div>
      </div>
      <span className="text-[10px] text-slate-400 font-medium">Pulse by ProjectAI</span>
    </div>
  </SidebarPage>
);

/* ════════════════════════════════════════════════════════════
   SLIDE 3: Project Attributes (User Flow Overview + User Growth)
   ════════════════════════════════════════════════════════════ */
const ProjectAttributesSlide = ({ data, project, pageNum, totalPages }) => {
  const m = data._publicStats?.matrix || {};
  const invLimit = project?.stakeholder_limit || '-';

  const rows = [
    { label: 'Pending Review', key: 'pending_review', cls: 'bg-amber-50/70 text-amber-700' },
    { spacer: true },
    { label: 'Invited', key: 'invited', cls: 'bg-emerald-50/50', note: `Limit: ${invLimit}` },
    { label: 'Reference', key: 'reference' },
    { label: 'Total', computed: true, cls: 'bg-slate-50 font-semibold border-t-2' },
    { divider: true },
    { label: 'Rejected', key: 'rejected', cls: 'text-red-700' },
    { label: 'Anonymous', key: 'anonymous', anon: true, cls: 'text-slate-500' },
  ];

  const growthData = extendGrowthToNow((data._publicStats?.growth_data || []).map(g => ({
    date: g.date,
    admin: g.cumulative_by_source?.admin || 0,
    user: g.cumulative_by_source?.user || 0,
    self: g.cumulative_by_source?.self || 0,
    anonymous: g.cumulative_by_source?.anonymous || 0,
    total: g.cumulative || 0,
  })));

  return (
    <ContentPage title={`${data.project_name} | Project Attributes`} date={data._dateFormatted} pageNum={pageNum} totalPages={totalPages}>
      <div className="grid grid-cols-[180px_1fr] gap-y-1 gap-x-6 text-xs mb-3">
        <span className="font-semibold text-slate-700">Project Name</span><span className="text-slate-600">{data.project_name}</span>
        <span className="font-semibold text-slate-700">Project Manager</span><span className="text-slate-600">{project?.project_manager || '-'}</span>
        <span className="font-semibold text-slate-700">Description</span><span className="text-slate-600 line-clamp-2 text-[11px]">{project?.description || '-'}</span>
      </div>
      <div className="grid grid-cols-2 gap-4 flex-1">
        {/* Stakeholder Identification Source */}
        <Card className="p-0">
          <CardHeader className="pb-1 px-3 pt-2"><CardTitle className="text-[11px] font-bold">Stakeholder Identification Source</CardTitle></CardHeader>
          <CardContent className="px-3 pb-2">
            <table className="w-full text-[10px]">
              <thead><tr className="bg-[#3D7A84] text-white"><th className="text-left px-2 py-1 rounded-tl w-[35%]">Category</th><th className="text-center px-2 py-1 w-[16%]">Total</th><th className="text-center px-2 py-1 w-[16%]">Admin</th><th className="text-center px-2 py-1 w-[16%]">User</th><th className="text-center px-2 py-1 w-[16%] rounded-tr">Self</th></tr></thead>
              <tbody>
                {rows.map((r, i) => {
                  if (r.spacer) return <tr key={i}><td colSpan={5} className="h-1 bg-slate-100 p-0" /></tr>;
                  if (r.divider) return <tr key={i}><td colSpan={5} className="h-1 bg-slate-100 p-0" /></tr>;
                  const d = r.computed
                    ? { total: (m.invited?.total || 0) + (m.reference?.total || 0), admin: (m.invited?.admin || 0) + (m.reference?.admin || 0), user: (m.invited?.user || 0) + (m.reference?.user || 0), self: (m.invited?.self || 0) + (m.reference?.self || 0) }
                    : m[r.key] || {};
                  return (
                    <tr key={r.label} className={r.cls || (i % 2 ? 'bg-white' : 'bg-slate-50/50')}>
                      <td className="px-2 py-1">{r.label}{r.note && <span className="text-[8px] text-slate-400 ml-1">({r.note})</span>}</td>
                      <td className="text-center px-2 py-1 font-medium">{d.total || 0}</td>
                      <td className="text-center px-2 py-1">{r.anon ? '-' : (d.admin || 0)}</td>
                      <td className="text-center px-2 py-1">{r.anon ? '-' : (d.user || 0)}</td>
                      <td className="text-center px-2 py-1">{r.anon ? '-' : (d.self || 0)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
        {/* User Growth */}
        <Card className="p-0 overflow-hidden">
          <CardHeader className="pb-1 px-3 pt-2"><CardTitle className="text-[11px] font-bold">User Growth</CardTitle></CardHeader>
          <CardContent className="px-3 pb-2">
            {growthData.length > 0 ? (
              <ResponsiveContainer width="100%" height={145}>
                <AreaChart data={growthData} margin={{ top: 5, right: 30, left: -15, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 6, fill: '#94a3b8', angle: -35, textAnchor: 'end' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} interval="preserveStartEnd" height={35} />
                  <YAxis tick={{ fontSize: 8, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                  <RechartsTooltip contentStyle={{ fontSize: 9, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                  <Area type="monotone" dataKey="admin" stackId="1" stroke="#5AA2AE" fill="#5AA2AE" fillOpacity={0.6} isAnimationActive={false} />
                  <Area type="monotone" dataKey="user" stackId="1" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.6} isAnimationActive={false} />
                  <Area type="monotone" dataKey="self" stackId="1" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.6} isAnimationActive={false} />
                  <Area type="monotone" dataKey="anonymous" stackId="1" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.4} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[180px] text-xs text-slate-400">No growth data</div>
            )}
            <div className="flex items-center gap-3 text-[8px] mt-1 px-1">
              {[['Admin', '#5AA2AE'], ['User', '#8b5cf6'], ['Self', '#f59e0b'], ['Anonymous', '#94a3b8']].map(([l, c]) => (
                <div key={l} className="flex items-center gap-1"><div className="w-2 h-2 rounded" style={{ backgroundColor: c }} /><span className="text-slate-500">{l}</span></div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </ContentPage>
  );
};

/* ════════════════════════════════════════════════════════════
   SLIDE 4: Executive Summary
   ════════════════════════════════════════════════════════════ */
const ExecSummarySlide = ({ data, pageNum, totalPages }) => {
  const part = data._dashParticipation || data.participation || {};
  const overallScore = data._dashOverallSentiment;
  const insights = data._insights;
  // Use the same driver_insights data as the Driver Analysis pages so scores match
  const drivers = (data.driver_insights || []).map(d => ({
    driver_name: d.driver,
    avg_score: d.avg,
    response_count: d.count,
  }));

  return (
    <ContentPage title={`${data.project_name} | Executive Summary`} date={data._dateFormatted} pageNum={pageNum} totalPages={totalPages}>
      <div className="flex flex-col h-full">
        {/* Overall Sentiment Hero */}
        <Card className="bg-gradient-to-br from-[#5AA2AE]/10 to-[#5AA2AE]/5 border-[#5AA2AE]/20 mb-2 flex-shrink-0">
          <CardContent className="py-2 px-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[9px] text-[#5AA2AE] font-medium mb-0.5">Overall Sentiment Score</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-slate-900">{formatScore(overallScore || 0)}</span>
                  <span className="text-sm text-slate-400">/10</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  [part.invited || 0, 'Invited', '#5AA2AE'],
                  [part.reference || 0, 'Reference', '#8b5cf6'],
                  [part.pending || part.pending_review || 0, 'Pending', '#f59e0b'],
                ].map(([v, l, c]) => (
                  <div key={l} className="text-center px-3 py-1 bg-white/50 rounded-lg">
                    <p className="text-lg font-bold" style={{ color: c }}>{v}</p>
                    <p className="text-[8px] text-slate-500">{l}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Three-column: Key Metrics | Driver Performance | Trend */}
        <div className="grid grid-cols-3 gap-2 mb-3 flex-shrink-0" style={{ minHeight: 180 }}>
          <KeyMetricsTile
            topMetrics={insights?.top_metrics}
            bottomMetrics={insights?.bottom_metrics}
            compact
          />
          {/* Driver Performance - compact gauges */}
          <Card data-testid="driver-performance-tile">
            <CardHeader className="pb-1 px-3 pt-2">
              <CardTitle className="flex items-center gap-1 text-[11px]">
                <BarChart3 className="w-3 h-3 text-[#5AA2AE]" />
                Driver Performance
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-2">
              <div className="space-y-1">
                {drivers.map(d => {
                  const pct = d.avg_score ? (d.avg_score / 10 * 100) : 0;
                  const color = getHeatBadgeColor(d.avg_score);
                  return (
                    <div key={d.driver_name} className="flex items-center gap-2">
                      <span className="text-[9px] text-slate-600 truncate w-[55%]">{d.driver_name}</span>
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                      </div>
                      <span className="text-[9px] font-bold w-6 text-right" style={{ color }}>{formatScore(d.avg_score)}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          {/* Trend */}
          <TrendChartCompact data={data._overallTrend} />
        </div>

        {/* AI Insights — constrained to remaining space */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <AIInsightsTile insights={insights?.insights} compact />
        </div>
      </div>
    </ContentPage>
  );
};

/* Compact Trend Chart for Exec Summary */
const TrendChartCompact = ({ data: trendData }) => {
  const chartData = extendTrendToNow(trendData?.months?.map((m, i) => {
    const pt = { month: m };
    trendData.series?.forEach(s => { pt[s.name] = s.data?.[i]; });
    return pt;
  }) || []);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-1 px-3 pt-2">
        <CardTitle className="flex items-center gap-1 text-[11px]">
          <TrendingUp className="w-3 h-3 text-[#5AA2AE]" />
          Trend
        </CardTitle>
        <CardDescription className="text-[8px]">{trendData?.topline_question?.heading || 'Top-Line Score'}</CardDescription>
      </CardHeader>
      <CardContent className="px-3 pb-2">
        {chartData.length > 0 && trendData?.series?.length > 0 ? (
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={chartData} margin={{ top: 5, right: 25, left: -15, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 6, fill: '#94a3b8', angle: -35, textAnchor: 'end' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} interval="preserveStartEnd" height={30} />
              <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fontSize: 7, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
              {trendData.series.map(s => <Line key={s.name} type="monotone" dataKey={s.name} stroke={s.color} strokeWidth={1.5} dot={{ r: 1.5 }} connectNulls isAnimationActive={false} />)}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[120px] text-[10px] text-slate-400">No trend data</div>
        )}
      </CardContent>
    </Card>
  );
};

/* ════════════════════════════════════════════════════════════
   SLIDE 5: Stakeholder Distribution (single legend at bottom)
   ════════════════════════════════════════════════════════════ */
const StakeholderDistSlide = ({ data, pageNum, totalPages }) => {
  const groups = data._dashSegmentsByType || [];
  const part = data._dashParticipation || {};
  const left = groups.filter((_, i) => i % 2 === 0);
  const right = groups.filter((_, i) => i % 2 === 1);

  return (
    <ContentPage title={`${data.project_name} | Stakeholder Distribution`} date={data._dateFormatted} pageNum={pageNum} totalPages={totalPages}>
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-2 mb-2 flex-shrink-0">
          <Users className="w-4 h-4 text-[#5AA2AE]" />
          <span className="text-xs font-bold text-slate-800">Stakeholder Distribution</span>
          <span className="text-[10px] text-slate-400 ml-2">{part.invited || 0} invited &bull; {part.reference || 0} reference</span>
        </div>
        <div className="grid grid-cols-2 gap-4 flex-1 overflow-hidden">
          <div className="space-y-3 overflow-hidden">
            {left.map(g => <SegmentBreakdownNoLegend key={g.name} segmentType={g} />)}
          </div>
          <div className="space-y-3 overflow-hidden">
            {right.map(g => <SegmentBreakdownNoLegend key={g.name} segmentType={g} />)}
          </div>
        </div>
        {/* Single legend at bottom */}
        <div className="flex items-center gap-4 text-[9px] pt-2 border-t mt-2 flex-shrink-0">
          <div className="flex items-center gap-1"><div className="w-2 h-2 rounded bg-emerald-500" /><span className="text-slate-500">Completed</span></div>
          <div className="flex items-center gap-1"><div className="w-2 h-2 rounded bg-[#5AA2AE]" /><span className="text-slate-500">Pending</span></div>
          <div className="flex items-center gap-1"><div className="w-2 h-2 rounded bg-purple-400" /><span className="text-slate-500">Reference</span></div>
          <span className="text-slate-400">Inv/Ref &bull; Completion %</span>
        </div>
      </div>
    </ContentPage>
  );
};

/* SegmentBreakdownChart without per-chart legend */
function SegmentBreakdownNoLegend({ segmentType }) {
  const segments = segmentType.segments || [];
  const total = segmentType.total_unique || segments.reduce((sum, s) => sum + s.total, 0);
  const sorted = [...segments].sort((a, b) => b.total - a.total);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-[11px] text-slate-700">{segmentType.name}</h4>
        <span className="text-[9px] text-slate-400">{total} stakeholders</span>
      </div>
      {sorted.slice(0, 8).map(seg => {
        const completionRate = seg.invited > 0 ? Math.round((seg.completed / seg.invited) * 100) : 0;
        return (
          <div key={seg.segment_id}>
            <div className="flex items-center justify-between text-[10px] mb-0.5">
              <span className="font-medium text-slate-600">{seg.segment_name}</span>
              <div className="flex items-center gap-2">
                <span className="text-slate-500">{seg.invited}<span className="text-slate-300">/</span>{seg.reference}</span>
                <span className={`font-medium ${completionRate >= 70 ? 'text-emerald-600' : completionRate >= 40 ? 'text-amber-600' : 'text-slate-400'}`}>{completionRate}%</span>
              </div>
            </div>
            <div className="flex h-3.5 rounded-md overflow-hidden bg-slate-100">
              <div className="h-full bg-emerald-500" style={{ width: `${total > 0 ? (seg.completed / total * 100) * 3 : 0}%`, minWidth: seg.completed > 0 ? 3 : 0 }} />
              <div className="h-full bg-[#5AA2AE]" style={{ width: `${total > 0 ? ((seg.invited - seg.completed) / total * 100) * 3 : 0}%`, minWidth: (seg.invited - seg.completed) > 0 ? 3 : 0 }} />
              <div className="h-full bg-purple-400" style={{ width: `${total > 0 ? (seg.reference / total * 100) * 3 : 0}%`, minWidth: seg.reference > 0 ? 3 : 0 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   SLIDE 6+: Trend Analysis — 2x2 grid per page (up to 4 segment types)
   ════════════════════════════════════════════════════════════ */
const TrendGridSlide = ({ data, segTrends, pageIndex, pageNum, totalPages }) => {
  const pageTrends = segTrends.slice(pageIndex * 4, (pageIndex + 1) * 4);

  const globalYMax = useMemo(() => {
    return 10;
  }, [segTrends]);

  return (
    <ContentPage title={`${data.project_name} | Trend Analysis`} date={data._dateFormatted} pageNum={pageNum} totalPages={totalPages}>
      <div className="flex items-center gap-2 mb-2 flex-shrink-0">
        <TrendingUp className="w-4 h-4 text-[#5AA2AE]" />
        <span className="text-xs font-bold text-slate-800">Trend Analysis</span>
        {segTrends.length > 4 && <span className="text-[9px] text-slate-400 ml-2">Page {pageIndex + 1} of {Math.ceil(segTrends.length / 4)}</span>}
      </div>
      <div className="grid grid-cols-2 grid-rows-2 gap-3 flex-1" style={{ height: 'calc(100% - 24px)' }}>
        {pageTrends.map(st => {
          const td = st.data || {};
          const chartData = extendTrendToNow((td.months || []).map((m, i) => {
            const pt = { month: m };
            (td.series || []).forEach(s => { pt[s.name] = s.data?.[i]; });
            return pt;
          }));
          const questionName = td.topline_question?.heading || 'Top-Line Score';
          const hasTrend = chartData.length > 0 && td.series?.length > 0;

          return (
            <Card key={st.id} className="flex flex-col p-0 overflow-hidden">
              <CardHeader className="pb-0 px-3 pt-2 flex-shrink-0">
                <CardTitle className="text-[10px] font-bold text-slate-700">{st.name}</CardTitle>
                <CardDescription className="text-[8px]">{questionName}</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 px-2 pb-1 min-h-0">
                {hasTrend ? (
                  <div className="h-full flex flex-col">
                    <div className="flex-1 min-h-0">
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={chartData} margin={{ top: 5, right: 25, left: -20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="month" tick={{ fontSize: 7, fill: '#94a3b8', angle: -35, textAnchor: 'end' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} interval="preserveStartEnd" height={45} />
                          <YAxis domain={[0, globalYMax]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fontSize: 7, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                          <RechartsTooltip contentStyle={{ fontSize: 9, borderRadius: 8 }} formatter={(v) => v !== null ? [v.toFixed(1), ''] : ['-', '']} />
                          {td.series.map(s => <Line key={s.name} type="monotone" dataKey={s.name} stroke={s.color} strokeWidth={1.5} dot={{ r: 2, fill: s.color }} connectNulls isAnimationActive={false} />)}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 px-1 flex-shrink-0">
                      {td.series.map(s => (
                        <div key={s.name} className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                          <span className="text-[7px] text-slate-600">{s.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-400">
                    <div className="text-center">
                      <Clock className="w-5 h-5 mx-auto mb-1 text-slate-300" />
                      <p className="text-[9px]">No trend data</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        {/* Fill empty grid cells if less than 4 */}
        {pageTrends.length < 4 && Array.from({ length: 4 - pageTrends.length }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}
      </div>
    </ContentPage>
  );
};

/* ════════════════════════════════════════════════════════════
   DRIVER SLIDES — Two-column tile layout matching app
   Top: Key AI insight banner (no repeated scores)
   Left 2/3: Two-column grid of question tiles
   Right 1/3: AI analysis sections (bigger fonts)
   ════════════════════════════════════════════════════════════ */
const DriverSlide = ({ data, driver, pageNum, totalPages }) => {
  const insightText = driver.insight || '';
  const sections = parseDriverInsight(insightText);
  const aiSummary = extractCleanSummary(insightText);

  return (
    <ContentPage title={`${data.project_name} | Driver Analysis | ${driver.driver}`} date={data._dateFormatted} pageNum={pageNum} totalPages={totalPages}>
      <div className="flex flex-col h-full gap-1.5">
        {/* Top: Key Insight banner — no repeated metrics */}
        <div className="bg-gradient-to-r from-[#EBF5F7] to-white border border-[#5AA2AE]/20 rounded-lg px-3 py-2 flex-shrink-0 flex items-start gap-2">
          <Sparkles className="w-3.5 h-3.5 text-[#5AA2AE] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-[9px] font-bold text-[#3D7A84] uppercase tracking-wider mb-0.5">Key Insight — {driver.driver}</p>
            <p className="text-[11px] text-slate-600 leading-relaxed">{aiSummary || 'No AI summary available for this driver.'}</p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <MiniDonut score={driver.avg} />
          </div>
        </div>

        {/* Main: Left 2/3 (2-col questions) + Right 1/3 (AI) */}
        <div className="flex gap-2 flex-1 min-h-0 overflow-hidden">
          {/* Left 2/3 — two columns of question tiles */}
          <div className="w-[62%] overflow-hidden">
            <div className="grid grid-cols-2 gap-1.5 h-full auto-rows-min overflow-hidden">
              {(driver.questions || []).slice(0, 8).map(q => {
                const cardData = {
                  ...q,
                  question_type: q.question_type || 'slider',
                  avg: q.avg,
                  scale_max: q.scale_max || 10,
                  response_count: q.count || q.response_count,
                  left_label: q.left_label,
                  right_label: q.right_label,
                  question_text: q.question_text,
                  std_dev: q.std_dev,
                  distribution: q.distribution,
                  total_mentions: q.total_mentions,
                  unique_topics: q.unique_topics,
                  avg_chars: q.avg_chars,
                  sentiment_breakdown: q.sentiment_breakdown,
                  themes: q.themes,
                };
                return <QuestionSummaryCard key={q.id} question={cardData} compact />;
              })}
            </div>
          </div>
          {/* Right 1/3 — AI Sections (bigger font, no scores) */}
          <div className="w-[38%] space-y-2 overflow-hidden pr-1">
            {[
              { key: 'positives', label: 'Strengths', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500' },
              { key: 'challenges', label: 'Challenges', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500' },
              { key: 'opportunities', label: 'Opportunities', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-500' },
              { key: 'outliers', label: 'Segment Outliers', color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200', dot: 'bg-purple-500' },
            ].map(({ key, label, color, bg, border, dot }) => {
              const items = (sections[key] || []).slice(0, 2);
              if (!items.length) return null;
              return (
                <div key={key} className={`${bg} border ${border} rounded-lg px-2 py-1.5`}>
                  <h4 className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${color}`}>{label}</h4>
                  {items.map((t, i) => (
                    <div key={i} className="flex items-start gap-1.5 mb-0.5">
                      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot}`} />
                      <p className="text-[10px] text-slate-600 leading-relaxed">{t}</p>
                    </div>
                  ))}
                </div>
              );
            })}
            {!sections.positives?.length && !sections.challenges?.length && !sections.opportunities?.length && (
              <div className="text-[10px] text-slate-400 italic mt-2 p-2 bg-slate-50 rounded-lg border border-slate-200">
                No AI analysis available for this driver.
              </div>
            )}
          </div>
        </div>
      </div>
    </ContentPage>
  );
};

/* Mini Donut — no label, no count, consistent size */
function MiniDonut({ score, maxScore = 10 }) {
  const pct = (score / maxScore) * 100;
  const color = getHeatBadgeColor(score);
  const size = 72;
  const r = 28;
  const sw = 7;
  const circ = 2 * Math.PI * r;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#e5e7eb" strokeWidth={sw} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={sw} fill="none"
          strokeDasharray={`${pct * circ / 100} ${circ}`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-bold" style={{ color }}>{formatScore(score)}</span>
      </div>
    </div>
  );
}

function extractCleanSummary(text) {
  if (!text) return '';
  const lines = text.split('\n').map(l => l.replace(/\*\*/g, '').trim()).filter(Boolean);
  const clean = [];
  for (const line of lines) {
    if (line.startsWith('#') || line.startsWith('---')) continue;
    if (/^\d+\.\d+\/10/.test(line)) continue;
    if (/^[-*]/.test(line)) continue;
    // Strip redundant prefixes
    let stripped = line
      .replace(/^Key Insight:?\s*/i, '')
      .replace(/^Opportunities for Improvement:?\s*/i, '')
      .replace(/^Positives\/Strengths:?\s*/i, '')
      .replace(/^Challenges\/Concerns:?\s*/i, '')
      .replace(/^Notable Segment Outliers:?\s*/i, '')
      .replace(/\d+\.\d+\/10/g, '')
      .replace(/\b\d+\.\d+\b/g, '')
      .trim();
    if (stripped.length > 20) {
      clean.push(stripped);
      if (clean.length >= 2) break;
    }
  }
  return clean.join(' ') || lines.slice(0, 2).join(' ').substring(0, 250);
}

function parseDriverInsight(text) {
  if (!text) return {};
  const lines = text.split('\n').map(l => l.replace(/\*\*/g, '').trim()).filter(Boolean);
  const result = { positives: [], challenges: [], opportunities: [], outliers: [] };
  let current = null;
  for (const line of lines) {
    const lower = line.toLowerCase();
    if (lower.includes('positive') || lower.includes('strength')) { current = 'positives'; continue; }
    if (lower.includes('challenge') || lower.includes('concern') || lower.includes('weakness')) { current = 'challenges'; continue; }
    if (lower.includes('opportunit') || lower.includes('improvement') || lower.includes('recommend')) { current = 'opportunities'; continue; }
    if (lower.includes('outlier') || lower.includes('notable segment')) { current = 'outliers'; continue; }
    if (current) {
      const clean = line.replace(/^[-*#\d.]+\s*/, '').trim();
      if (clean.length > 5) result[current].push(clean);
    }
  }
  return result;
}

/* ════════════════════════════════════════════════════════════
   SEGMENT ANALYSIS — Heatmap per type (Page 1)
   5-level color scheme matching the app heatmap
   ════════════════════════════════════════════════════════════ */
const HEAT_COLORS = {
  veryLow: '#ef4444',   // <4
  low: '#f97316',        // 4-5.5
  medium: '#fbbf24',     // 5.5-6.5
  good: '#84cc16',       // 6.5-7.5
  veryGood: '#22c55e',   // >7.5
};
const getHeatColor = (v) => {
  if (v == null) return { bg: '#f8fafc', fg: '#94a3b8' };
  if (v < 4) return { bg: HEAT_COLORS.veryLow, fg: '#ffffff' };
  if (v < 5.5) return { bg: HEAT_COLORS.low, fg: '#ffffff' };
  if (v < 6.5) return { bg: HEAT_COLORS.medium, fg: '#78350f' };
  if (v < 7.5) return { bg: HEAT_COLORS.good, fg: '#ffffff' };
  return { bg: HEAT_COLORS.veryGood, fg: '#ffffff' };
};
const getHeatBadgeColor = (v) => {
  if (v == null) return '#94a3b8';
  if (v < 4) return HEAT_COLORS.veryLow;
  if (v < 5.5) return HEAT_COLORS.low;
  if (v < 6.5) return HEAT_COLORS.medium;
  if (v < 7.5) return HEAT_COLORS.good;
  return HEAT_COLORS.veryGood;
};

const SegmentHeatmapSlide = ({ data, segTypeGroup, pageNum, totalPages }) => {
  const grid = data.heatmap_grid || {};
  // Deduplicate questions by heading (prefer the first occurrence)
  const allQs = data.heatmap_questions || [];
  const seen = new Set();
  const qs = allQs.filter(q => {
    if (seen.has(q.heading)) return false;
    seen.add(q.heading);
    return true;
  });
  const segs = segTypeGroup.segments || [];

  return (
    <ContentPage title={`${data.project_name} | Segment Analysis | ${segTypeGroup.type_name}`} date={data._dateFormatted} pageNum={pageNum} totalPages={totalPages}>
      <div className="h-full flex flex-col">
        <div className="flex-1 overflow-auto">
          <table className="w-full text-[9px] border-collapse" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 120 }} />
              {qs.map(q => <col key={q.id} />)}
            </colgroup>
            <thead>
              <tr>
                <th className="text-left px-2 bg-[#3D7A84] text-white font-semibold sticky left-0 z-10 align-bottom pb-2 border-r border-white/20" style={{ height: 95 }}>
                  <span className="text-[9px]">Segment</span>
                </th>
                {qs.map(q => (
                  <th key={q.id} className="bg-[#3D7A84] text-center align-bottom pb-2 px-0.5 border-r border-white/10" style={{ height: 95 }}>
                    <div className="flex items-end justify-center h-full pb-1">
                      <span className="text-white font-normal text-[7px] leading-tight block"
                        style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', maxHeight: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {q.heading}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {segs.map((seg, si) => {
                const scores = grid[seg.name] || {};
                return (
                  <tr key={seg.name}>
                    <td className="px-2 py-1.5 font-semibold text-slate-700 sticky left-0 z-10 border-b border-slate-200 border-r border-r-slate-100" style={{ backgroundColor: si % 2 ? 'white' : '#f8fafc' }}>
                      {seg.name} <span className="text-slate-400 font-normal text-[8px]">({seg.count})</span>
                    </td>
                    {qs.map(q => {
                      const v = scores[q.id];
                      const { bg, fg } = getHeatColor(v);
                      return (
                        <td key={q.id} className="text-center py-1.5 font-bold border-b border-white/50"
                          style={{ color: fg, backgroundColor: bg, fontSize: 10 }}>
                          {v != null ? v.toFixed(1) : '--'}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Legend */}
        <div className="flex items-center justify-center gap-3 pt-2 border-t border-slate-100 flex-shrink-0">
          {[
            { label: '<4', color: HEAT_COLORS.veryLow },
            { label: '4-5.5', color: HEAT_COLORS.low },
            { label: '5.5-6.5', color: HEAT_COLORS.medium },
            { label: '6.5-7.5', color: HEAT_COLORS.good },
            { label: '>7.5', color: HEAT_COLORS.veryGood },
          ].map(({ label, color }) => (
            <div key={label} className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: color }} />
              <span className="text-[8px] text-slate-500">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </ContentPage>
  );
};

/* ════════════════════════════════════════════════════════════
   SEGMENT ANALYSIS — AI Table per type (Page 2)
   ════════════════════════════════════════════════════════════ */
const SegmentAITableSlide = ({ data, segTypeName, segAnalysis, segTypeGroup, pageNum, totalPages }) => {
  const segments = segAnalysis?.segments || [];
  const segs = segTypeGroup?.segments || [];
  const topQ = data._insights?.top_metrics?.[0];
  const topQId = topQ?.question_id;
  const grid = data.heatmap_grid || {};
  const isLoading = false; // Data is now fully loaded before preview

  // If no AI yet, build placeholder rows from segTypeGroup
  const displaySegments = segments.length > 0
    ? segments
    : segs.map(s => ({ segment_name: s.name, count: s.count, positives: '', challenges: '', opportunities: '' }));

  return (
    <ContentPage title={`${data.project_name} | Segment Analysis | ${segTypeName} | AI Summary`} date={data._dateFormatted} pageNum={pageNum} totalPages={totalPages}>
      <div className="h-full overflow-auto">
        <table className="w-full text-[9px] border-collapse">
          <thead>
            <tr className="bg-[#3D7A84] text-white">
              <th className="text-left px-2 py-1.5 font-semibold w-[20%] rounded-tl">Segment</th>
              <th className="text-left px-2 py-1.5 font-semibold w-[26%]">Positives</th>
              <th className="text-left px-2 py-1.5 font-semibold w-[26%]">Challenges</th>
              <th className="text-left px-2 py-1.5 font-semibold w-[28%] rounded-tr">Opportunities</th>
            </tr>
          </thead>
          <tbody>
            {displaySegments.map((seg, i) => {
              const segScore = topQId ? grid[seg.segment_name]?.[topQId] : null;
              const scoreColor = getHeatBadgeColor(segScore);
              return (
                <tr key={seg.segment_name} className={i % 2 ? 'bg-white' : 'bg-slate-50/50'}>
                  <td className="px-2 py-1.5 font-semibold text-slate-700 align-top border-b border-slate-100">
                    <div className="flex items-center gap-1.5">
                      <span>{seg.segment_name}</span>
                      {segScore != null && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: scoreColor }}>
                          {segScore.toFixed(1)}
                        </span>
                      )}
                    </div>
                    <span className="block text-[8px] text-slate-400 font-normal">n={seg.count}</span>
                  </td>
                  <td className={`px-2 py-1.5 align-top border-b border-slate-100 leading-relaxed ${!seg.positives ? 'text-slate-400 italic' : 'text-slate-600'}`}><span>{seg.positives || (seg.insufficient ? 'Insufficient data' : 'Generating...')}</span></td>
                  <td className={`px-2 py-1.5 align-top border-b border-slate-100 leading-relaxed ${!seg.challenges ? 'text-slate-400 italic' : 'text-slate-600'}`}><span>{seg.challenges || (seg.insufficient ? 'Insufficient data' : 'Generating...')}</span></td>
                  <td className={`px-2 py-1.5 align-top border-b border-slate-100 leading-relaxed ${!seg.opportunities ? 'text-slate-400 italic' : 'text-slate-600'}`}><span>{seg.opportunities || (seg.insufficient ? 'Insufficient data' : 'Generating...')}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </ContentPage>
  );
};

/* ════════════════════════════════════════════════════════════
   PUBLIC ANALYSIS — Same format as Driver Analysis pages
   Left 2/3: question tiles, Right 1/3: AI themes/sentiment
   ════════════════════════════════════════════════════════════ */
const PublicAnalysisSlide = ({ data, pageNum, totalPages, pageIndex = 0 }) => {
  const publicQs = data.public_questions || [];
  if (publicQs.length === 0) return null;
  const perPage = 8;
  const pageQs = publicQs.slice(pageIndex * perPage, (pageIndex + 1) * perPage);

  // Collect themes and sentiment from public questions
  const allThemes = pageQs.flatMap(q => q.themes || []);
  const sentimentBreakdown = pageQs.reduce((acc, q) => {
    if (q.sentiment_breakdown) {
      Object.entries(q.sentiment_breakdown).forEach(([k, v]) => {
        acc[k] = (acc[k] || 0) + v;
      });
    }
    return acc;
  }, {});

  return (
    <ContentPage title={`${data.project_name} | Public Analysis${publicQs.length > perPage ? ` (${pageIndex + 1})` : ''}`} date={data._dateFormatted} pageNum={pageNum} totalPages={totalPages}>
      <div className="flex flex-col h-full gap-1.5">
        {/* Top: Summary banner */}
        <div className="bg-gradient-to-r from-[#EBF5F7] to-white border border-[#5AA2AE]/20 rounded-lg px-3 py-2 flex-shrink-0 flex items-start gap-2">
          <Globe className="w-3.5 h-3.5 text-[#5AA2AE] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-[9px] font-bold text-[#3D7A84] uppercase tracking-wider mb-0.5">Public Landing Page Analysis</p>
            <p className="text-[11px] text-slate-600 leading-relaxed">Questions presented to external stakeholders and the public via the project landing page.</p>
          </div>
        </div>

        {/* Main: Left 2/3 (tiles) + Right 1/3 (themes) */}
        <div className="flex gap-2 flex-1 min-h-0 overflow-hidden">
          <div className="w-[62%] overflow-hidden">
            <div className="grid grid-cols-2 gap-1.5 h-full auto-rows-min overflow-hidden">
              {pageQs.map(q => {
                const cardData = {
                  ...q,
                  question_type: q.question_type || 'free_text',
                  avg: q.avg,
                  scale_max: q.scale_max || 10,
                  response_count: q.response_count || q.count || 0,
                  left_label: q.left_label,
                  right_label: q.right_label,
                  question_text: q.question_text,
                  std_dev: q.std_dev,
                  distribution: q.distribution,
                  total_mentions: q.total_mentions,
                  unique_topics: q.unique_topics,
                  avg_chars: q.avg_chars,
                  sentiment_breakdown: q.sentiment_breakdown,
                  themes: q.themes,
                };
                return <QuestionSummaryCard key={q.id} question={cardData} compact />;
              })}
            </div>
          </div>
          <div className="w-[38%] space-y-2 overflow-y-auto pr-1">
            {allThemes.length > 0 && (
              <div className="border border-[#5AA2AE]/20 rounded-lg p-2 bg-[#EBF5F7]/30">
                <h4 className="text-[9px] font-bold text-[#3D7A84] uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Key Themes
                </h4>
                <ul className="space-y-1">
                  {allThemes.slice(0, 8).map((t, i) => (
                    <li key={i} className="text-[10px] text-slate-600 leading-snug flex items-start gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-[#5AA2AE] mt-1.5 flex-shrink-0" />
                      {typeof t === 'string' ? t : t.theme || t.text || JSON.stringify(t)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {Object.keys(sentimentBreakdown).length > 0 && (
              <div className="border border-slate-200 rounded-lg p-2 bg-slate-50/50">
                <h4 className="text-[9px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">Sentiment</h4>
                <div className="space-y-1">
                  {Object.entries(sentimentBreakdown).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between">
                      <span className="text-[9px] text-slate-500 capitalize">{k}</span>
                      <span className="text-[9px] font-semibold text-slate-700">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {allThemes.length === 0 && Object.keys(sentimentBreakdown).length === 0 && (
              <div className="text-[10px] text-slate-400 italic p-3 text-center">
                Analysis data will appear here when public responses are collected.
              </div>
            )}
          </div>
        </div>
      </div>
    </ContentPage>
  );
};

/* ════════════════════════════════════════════════════════════
   CLOSING
   ════════════════════════════════════════════════════════════ */
const ClosingSlide = () => (
  <div className="h-full w-full flex flex-col items-center relative" style={{ backgroundColor: '#4F8B91', fontFamily: 'Inter, sans-serif' }}>
    <div style={{ flex: '0 0 32%' }} />
    <img src="/pulse-square-logo.png" alt="Pulse" className="rounded-xl" style={{ height: 80, width: 80 }} />
    <img src="/pulse-text-white.png" alt="pulse" className="object-contain mt-4 opacity-95" style={{ height: 104 }} />
    <p className="mt-3 tracking-wide" style={{ fontSize: 15, color: 'rgba(255,255,255,0.45)' }}>people are everything.</p>
    <div className="flex-1" />
    <div className="pb-6">
      <img src="/projectai-logo-white.png" alt="ProjectAI" className="h-5 opacity-60" />
    </div>
  </div>
);

/* ════════════════════════════════════════════════════════════
   MAIN CAROUSEL
   ════════════════════════════════════════════════════════════ */
/* ─── Build slides array (shared between carousel & print page) ─── */
export function buildReportSlides(data, project) {
    const list = [];

    list.push({ key: 'cover', el: <CoverSlide data={data} />, noPage: true });
    list.push({ key: 'disclaimer', el: <DisclaimerSlide />, noPage: true });
    list.push({ key: 'attrs', render: (pn, tp) => <ProjectAttributesSlide data={data} project={project} pageNum={pn} totalPages={tp} /> });
    list.push({ key: 'exec', render: (pn, tp) => <ExecSummarySlide data={data} pageNum={pn} totalPages={tp} /> });

    if ((data._dashSegmentsByType || []).length > 0) {
      list.push({ key: 'dist', render: (pn, tp) => <StakeholderDistSlide data={data} pageNum={pn} totalPages={tp} /> });
    }

    const segTrends = data._segmentTrends || [];
    const trendsWithData = segTrends.filter(st => st.data?.months?.length > 0 && st.data?.series?.length > 0);
    const effectiveTrends = trendsWithData.length > 0
      ? trendsWithData
      : data._overallTrend?.months?.length > 0
        ? [{ id: 'overall', name: 'Overall', data: data._overallTrend }]
        : [];
    if (effectiveTrends.length > 0) {
      const trendPages = Math.ceil(effectiveTrends.length / 4);
      for (let i = 0; i < trendPages; i++) {
        list.push({ key: `trend_page_${i}`, render: (pn, tp) => <TrendGridSlide data={data} segTrends={effectiveTrends} pageIndex={i} pageNum={pn} totalPages={tp} /> });
      }
    }

    (data.driver_insights || []).forEach((di, i) => {
      list.push({ key: `driver_${i}`, render: (pn, tp) => <DriverSlide data={data} driver={di} pageNum={pn} totalPages={tp} /> });
    });

    const segTypeGroups = data.segment_type_groups || [];
    const segAnalysisArr = data._segmentAnalysis || [];
    segTypeGroups.forEach(stg => {
      if ((stg.segments || []).length > 0) {
        list.push({ key: `seg_heat_${stg.type_name}`, render: (pn, tp) => <SegmentHeatmapSlide data={data} segTypeGroup={stg} pageNum={pn} totalPages={tp} /> });
        const analysis = segAnalysisArr.find(sa => sa.segment_type_name === stg.type_name);
        list.push({ key: `seg_ai_${stg.type_name}`, render: (pn, tp) => <SegmentAITableSlide data={data} segTypeName={stg.type_name} segAnalysis={analysis} segTypeGroup={stg} pageNum={pn} totalPages={tp} /> });
      }
    });

    if ((data.public_questions || []).length > 0) {
      const publicPages = Math.ceil(data.public_questions.length / 8);
      for (let i = 0; i < publicPages; i++) {
        list.push({ key: `public_${i}`, render: (pn, tp) => <PublicAnalysisSlide data={data} pageNum={pn} totalPages={tp} pageIndex={i} /> });
      }
    }

    list.push({ key: 'close', el: <ClosingSlide />, noPage: true });

    const totalPages = list.length;
    return list.map((item, idx) => {
      if (item.el) return { key: item.key, el: item.el };
      return { key: item.key, el: item.render(idx + 1, totalPages) };
    });
}

export const ReportPreview = ({ data, project, onClose, pregenTaskId = null }) => {
  const [current, setCurrent] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [printProgress, setPrintProgress] = useState('');

  // Build slides first so handleDownloadPDF can reference them
  const slides = useMemo(() => buildReportSlides(data, project), [data, project]);

  const handleDownloadPDF = async () => {
    if (downloading) return;
    setDownloading(true);
    setPrintProgress('Checking for PDF...');
    const token = localStorage.getItem('pulse_token');
    const API = process.env.REACT_APP_BACKEND_URL;
    const pid = project?.id;

    try {
      let taskId = pregenTaskId;

      // Check if pre-generated PDF is already done
      if (taskId) {
        try {
          const statusRes = await fetch(`${API}/api/projects/${pid}/pdf-status/${taskId}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (statusRes.ok) {
            const sd = await statusRes.json();
            if (sd.status === 'done') {
              setPrintProgress('Downloading...');
              await _downloadPdf(token, API, pid, taskId);
              setPrintProgress('PDF downloaded!');
              await new Promise(r => setTimeout(r, 1500));
              setDownloading(false);
              setPrintProgress('');
              return;
            } else if (sd.status !== 'error') {
              // Still generating — poll from here
              await _pollAndDownload(token, API, pid, taskId);
              return;
            }
          }
        } catch (_) { /* fall through */ }
        taskId = null;
      }

      // No pre-generated PDF — start on-demand
      setPrintProgress('Saving report data...');
      await fetch(`${API}/api/projects/${pid}/save-report-snapshot`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_data: data, project_info: project }),
      });
      setPrintProgress('Starting PDF generation...');
      const startRes = await fetch(`${API}/api/projects/${pid}/generate-pdf-v2`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!startRes.ok) throw new Error(`Server returned ${startRes.status}`);
      const result = await startRes.json();
      await _pollAndDownload(token, API, pid, result.task_id);
    } catch (err) {
      console.error('PDF download error:', err);
      setPrintProgress(`Error: ${err.message}`);
      await new Promise(r => setTimeout(r, 3000));
      setDownloading(false);
      setPrintProgress('');
    }
  };

  const _pollAndDownload = async (token, API, pid, taskId) => {
    const STATUS_MSGS = {
      starting: 'Starting PDF engine...',
      launching_browser: 'Launching renderer...',
      setting_auth: 'Authenticating...',
      loading_page: 'Loading report page...',
      waiting_for_render: 'Rendering slides...',
      rendering_pdf: 'Generating PDF...',
      done: 'Downloading...',
    };
    let status = 'starting';
    let pollFailures = 0;
    while (status !== 'done' && status !== 'error') {
      await new Promise(r => setTimeout(r, 2500));
      try {
        const pollRes = await fetch(`${API}/api/projects/${pid}/pdf-status/${taskId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if ([520, 502, 504].includes(pollRes.status)) {
          pollFailures++;
          if (pollFailures >= 20) throw new Error('Server temporarily unavailable');
          continue;
        }
        if (!pollRes.ok) throw new Error(`Status check: ${pollRes.status}`);
        const pollData = await pollRes.json();
        status = pollData.status;
        pollFailures = 0;
        setPrintProgress(STATUS_MSGS[status] || `Processing (${status})...`);
        if (status === 'error') throw new Error(pollData.error || 'PDF generation failed on server');
      } catch (pollErr) {
        if (pollErr.message?.includes('PDF generation failed')) throw pollErr;
        pollFailures++;
        if (pollFailures >= 20) throw new Error('Server temporarily unavailable');
      }
    }
    setPrintProgress('Downloading...');
    await _downloadPdf(token, API, pid, taskId);
    setPrintProgress('PDF downloaded!');
    await new Promise(r => setTimeout(r, 1500));
    setDownloading(false);
    setPrintProgress('');
  };

  const _downloadPdf = async (token, API, pid, taskId) => {
    const dlRes = await fetch(`${API}/api/projects/${pid}/download-pdf/${taskId}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!dlRes.ok) throw new Error('Failed to download PDF');
    const blob = await dlRes.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const today = new Date().toISOString().slice(0, 10);
    const projName = (project?.name || 'Report').replace(/\s+/g, '_');
    a.download = `Pulse_${projName}_${today}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const prev = useCallback(() => setCurrent(c => Math.max(0, c - 1)), []);
  const next = useCallback(() => setCurrent(c => Math.min(slides.length - 1, c + 1)), [slides.length]);

  useEffect(() => {
    const h = e => { if (e.key === 'ArrowLeft') prev(); else if (e.key === 'ArrowRight') next(); else if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [prev, next, onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center" data-testid="report-preview-overlay">
      <button onClick={onClose} data-testid="report-preview-close" className="absolute top-3 right-3 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition"><X className="w-5 h-5" /></button>
      <button onClick={handleDownloadPDF} disabled={downloading} data-testid="report-download-pdf" className="absolute top-3 right-14 z-50 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-medium transition flex items-center gap-1.5 disabled:opacity-50">
        {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        {downloading ? (printProgress || 'Generating...') : 'Download PDF'}
      </button>
      <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50 text-white/50 text-xs font-mono">{current + 1} / {slides.length}</div>
      {current > 0 && <button onClick={prev} data-testid="report-preview-prev" className="absolute left-2 top-1/2 -translate-y-1/2 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition"><ChevronLeft className="w-6 h-6" /></button>}
      {current < slides.length - 1 && <button onClick={next} data-testid="report-preview-next" className="absolute right-2 top-1/2 -translate-y-1/2 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition"><ChevronRight className="w-6 h-6" /></button>}

      <div className="bg-white rounded shadow-2xl overflow-hidden" style={{ width: 1120, height: Math.round(1120 / A4_RATIO), maxHeight: 'calc(100vh - 48px)', maxWidth: 'calc(100vw - 64px)' }} data-testid="report-preview-slide">
        {slides[current]?.el}
      </div>

      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-50">
        {slides.map((s, i) => <button key={s.key} onClick={() => setCurrent(i)} className={`w-1.5 h-1.5 rounded-full transition-all ${i === current ? 'bg-white scale-150' : 'bg-white/20 hover:bg-white/40'}`} />)}
      </div>
    </div>,
    document.body
  );
};

export default ReportPreview;
